import { FC, useState } from 'react'
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { produtoService } from '@/services/produtoService'
import type { Produto } from '@/types/produto'

const TEXTO_ERRO_ATIVO: Record<string, string> = {
  MOTIVO_OBRIGATORIO: 'Informe o motivo para inativar o produto.',
  PRODUTO_NAO_ENCONTRADO: 'Este produto não existe mais. Volte à lista de produtos.',
  ACCESS_DENIED: 'Só o administrador ativa ou inativa produtos.',
}

/** Mensagem legível do erro do PATCH (403 = não é ADMIN; o resto vem do servidor). */
export const mensagemErroAtivo = (e: unknown): string => {
  const codigo = (e as { data?: { errorCode?: unknown } } | null)?.data?.errorCode
  if (typeof codigo === 'string' && TEXTO_ERRO_ATIVO[codigo]) return TEXTO_ERRO_ATIVO[codigo]
  if ((e as { status?: number } | null)?.status === 403) return TEXTO_ERRO_ATIVO.ACCESS_DENIED

  const m = (e as { message?: unknown } | null)?.message
  return typeof m === 'string' && m.trim() ? m : 'Não foi possível alterar o status do produto.'
}

interface Props {
  produto: Produto
  /** ADMIN altera (AN-11 §2.1: `PATCH …/ativo` só ADMIN); o resto do back-office só lê. */
  podeAlterar: boolean
  onAlterado: (produto: Produto) => void
}

/**
 * Switch "Ativo" do cabeçalho do produto (Onda 5 — AN-11 §2.3). Alterar pede confirmação;
 * inativar exige motivo (≤500, auditoria `ALTERAR_ATIVO`; 422 `MOTIVO_OBRIGATORIO`), ativar
 * aceita motivo opcional. Inativar não mexe nas reservas existentes
 * (Q-AN11-6): só impede novas — o servidor recusa com 422 `PRODUTO_INATIVO`.
 *
 * GERENTE/OPERADOR veem o status como selo, sem o controle. O consultor não vê nada (a lista
 * dele só traz ativos).
 */
export const SwitchAtivoProduto: FC<Props> = ({ produto, podeAlterar, onAlterado }) => {
  const [destino, setDestino] = useState<boolean | null>(null)
  const [motivo, setMotivo] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  if (!podeAlterar) {
    return (
      <Chip
        size="small"
        color={produto.ativo ? 'success' : 'default'}
        label={produto.ativo ? 'Ativo' : 'Inativo'}
        aria-label={`Status do produto: ${produto.ativo ? 'ativo' : 'inativo'}`}
      />
    )
  }

  const abrir = (novo: boolean) => {
    setDestino(novo)
    setMotivo('')
    setErro(null)
  }

  const confirmar = async () => {
    if (destino === null) return
    setSalvando(true)
    setErro(null)
    try {
      const atualizado = await produtoService.alterarAtivo(produto.id, {
        ativo: destino,
        motivo: motivo.trim() || null,
      })
      // Defesa: um backend que responda sem corpo não pode desligar o switch errado.
      onAlterado(atualizado && typeof atualizado === 'object' ? atualizado : { ...produto, ativo: destino })
      setDestino(null)
    } catch (e) {
      setErro(mensagemErroAtivo(e))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <>
      <FormControlLabel
        control={
          <Switch
            checked={produto.ativo}
            onChange={(_, marcado) => abrir(marcado)}
            inputProps={{ 'aria-label': 'Produto ativo' }}
          />
        }
        label={produto.ativo ? 'Ativo' : 'Inativo'}
      />
      <Dialog open={destino !== null} onClose={salvando ? undefined : () => setDestino(null)} fullWidth maxWidth="sm">
        <DialogTitle>{destino ? 'Ativar o produto?' : 'Inativar o produto?'}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
          <Typography variant="body2" color="text.secondary">
            {destino
              ? `"${produto.nome}" volta a aparecer para os consultores e a aceitar novas reservas.`
              : `"${produto.nome}" deixa de aparecer para os consultores e não aceita novas reservas. As reservas que já existem seguem o fluxo normal.`}
          </Typography>
          {erro && <Alert severity="error">{erro}</Alert>}
          <TextField
            label={destino ? 'Motivo (opcional)' : 'Motivo'}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            inputProps={{ maxLength: 500 }}
            multiline
            minRows={2}
            fullWidth
            required={!destino}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDestino(null)} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={destino ? 'primary' : 'warning'}
            onClick={() => void confirmar()}
            disabled={salvando || (!destino && !motivo.trim())}
          >
            {salvando ? 'Salvando…' : destino ? 'Ativar' : 'Inativar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default SwitchAtivoProduto
